// A product's links to other entities, as the rows of its relation tables
// and the makers of its head. Pure functions: the sections of the view are
// built from them.

import {
  DerivedMark,
  EntityRef,
  FactSource,
  LocationPrecision,
  OrganizationRole,
  ProductDetail,
  ProductLink,
  ProductLinkGroup,
  ProductOrganization,
  ProductOrganizationGroup,
  ProductStructure,
  ProductStructureLink,
  ProductTypicalHost,
} from "../../types/productPlane";
import { isInheritedBasis } from "../Detail/derivedBasis";
import { codeLabel, codeWords, formatQuantity } from "../Detail/format";
import { unnamedName } from "../Explore/productFields";

// The fixed order of the roles: who makes a product comes before who buys it
export const ROLE_ORDER: OrganizationRole[] = [
  "maker",
  "developer",
  "operator",
  "buyer",
  "funder",
  "owner",
  "other",
];

// One row of a relation table: the linked entity, how it is linked, what
// is said of the link, and what its own record says of it. The fields a
// kind of entity has no use for are left out.
export interface RelationRow {
  // Unique within the table
  key: string;
  entity: EntityRef;
  // How it is linked, as a label: "Procured under", "Built into"; for an
  // entity linked under several relations, their labels read together
  relation: string;
  // Each label of an entity linked under several relations, in order
  relations?: string[];
  // The broad kind of a component's class, which groups the components
  group?: EntityRef | null;
  // A line beneath the name where a row says more of what it is
  // ("unnamed · Modem module", "3 unnamed")
  note?: string | null;
  // How many unnamed entities of one class the row stands for
  count?: number;
  // Set when no document states the relation as such
  derived: { basis: string | null } | null;
  // The relation in a sentence
  summary: string | null;
  sources: FactSource[];
  // The newest date among the sources, YYYY-MM-DD
  latest: string | null;
  // The curated class of a linked product
  category?: EntityRef | null;
  // More on the link: the role of a component, the context of a variant
  detail?: string | null;
  // A stated number of units, and how it reads
  quantity?: number | null;
  quantityText?: string | null;
  // From the linked entity's own record
  types?: string[];
  status?: string[];
  start?: string | null;
  end?: string | null;
  severity?: string | null;
  countries?: string[];
  // Where a facility stands, and how exactly
  latitude?: number | null;
  longitude?: number | null;
  locationPrecision?: LocationPrecision | null;
  address?: string | null;
}

// The newest date among the sources of a fact
const latestOf = (sources: FactSource[]): string | null =>
  sources.reduce<string | null>(
    (latest, source) =>
      source.date && (!latest || source.date > latest) ? source.date : latest,
    null,
  );

// The larger stated quantity first, then the better sourced; a quantity in
// words alone counts as none
const byWeight = (left: ProductLink, right: ProductLink): number =>
  (right.quantity ?? -1) - (left.quantity ?? -1) ||
  right.sources.length - left.sources.length;

// What a programme, incident or facility is to the product, by the
// relation: the programme it was procured under is its "Procurement", the
// incident it was the object of has it "Targeted". A relation the list does
// not know reads as its words.
const LINK_ROLES: Record<string, string> = {
  developed_under: "Development",
  procured_under: "Procurement",
  supplied_to: "Supply",
  tested_in: "Testing",
  tested_under: "Testing",
  used_in: "Used",
  targeted_in: "Targeted",
  involved_in: "Involved",
  // A facility's table says what it is: its role need not say "site"
  produced_at: "Production",
  tested_at: "Testing",
  deployed_at: "Deployment",
  other: "Other",
};

export const linkRole = (relation: string): string =>
  LINK_ROLES[relation] ?? codeLabel(relation);

// Every role a row's entity plays: the roles of a merged row, else its one
export const rolesOf = (row: RelationRow): string[] => row.relations ?? [row.relation];

// A link as a row, with its one role
const linkRow = (relation: string, item: ProductLink): RelationRow => ({
  key: item.entity.id,
  entity: item.entity,
  relation: linkRole(relation),
  relations: [linkRole(relation)],
  derived: item.derived,
  summary: item.summary,
  sources: item.sources,
  latest: latestOf(item.sources),
  quantity: item.quantity,
  quantityText: formatQuantity(item.quantity, item.quantity_wording) || null,
  types: item.types,
  status: item.status,
  start: item.start,
  end: item.end,
  severity: item.severity,
  countries: item.countries,
  // A response from before the location is read has none
  latitude: item.latitude ?? null,
  longitude: item.longitude ?? null,
  locationPrecision: item.location_precision ?? null,
  address: item.address ?? null,
});

// One row of an entity linked twice: both roles, read together; its
// sentences, each once; the sources of both, each document once; the
// larger stated quantity; derived only where neither link is stated
const mergeLinkRows = (row: RelationRow, other: RelationRow): RelationRow => {
  const relations = Array.from(new Set([...rolesOf(row), ...rolesOf(other)]));
  const seen = new Set(row.sources.map((source) => source.document_id));
  const sources = [
    ...row.sources,
    ...other.sources.filter((source) => !seen.has(source.document_id)),
  ];
  const larger = (other.quantity ?? -1) > (row.quantity ?? -1) ? other : row;
  return {
    ...row,
    relation: relations.join(", "),
    relations,
    derived: row.derived && other.derived ? row.derived : null,
    summary:
      Array.from(new Set([row.summary, other.summary].filter(Boolean))).join(" ") || null,
    sources,
    latest: latestOf(sources),
    quantity: larger.quantity,
    quantityText: larger.quantityText,
  };
};

// Programmes, incidents or facilities as rows, one per entity: relation by
// relation and within a relation by weight, an entity linked under several
// relations at its first, with all its roles
export const linkRows = (groups: ProductLinkGroup[]): RelationRow[] => {
  const rows = new Map<string, RelationRow>();
  for (const group of groups) {
    for (const item of [...group.items].sort(byWeight)) {
      const row = linkRow(group.relation, item);
      const earlier = rows.get(item.entity.id);
      rows.set(item.entity.id, earlier ? mergeLinkRows(earlier, row) : row);
    }
  }
  return Array.from(rows.values());
};

// What the page calls the product: its name, else its class
export const detailName = (product: ProductDetail): string =>
  product.name ?? unnamedName(product.categories[0]?.category.name);

// The other product of a link, named by its class where no source names it
export const linkedProduct = (link: ProductStructureLink): EntityRef => ({
  ...link.product,
  name: link.product.name ?? unnamedName(link.category?.name),
});

const structureRow =
  (relation: string) =>
  (link: ProductStructureLink, index: number): RelationRow => ({
    key: `${relation}-${link.product.id}-${index}`,
    entity: linkedProduct(link),
    relation,
    derived: null,
    summary: link.summary,
    sources: link.sources,
    latest: latestOf(link.sources),
    category: link.category,
    detail: [link.role, link.context].filter(Boolean).join(" · ") || null,
  });

// How a link that is neither a component nor a variant reads from the
// product's point of view: "Uses", or "Used by" when the other product is
// the subject of the relation
export const otherLabel = (link: ProductStructureLink): string => {
  if (link.relation === "uses") {
    return link.direction === "outgoing" ? "Uses" : "Used by";
  }
  const label = codeLabel(link.relation);
  return link.direction === "outgoing" ? label : `${label} (from the other)`;
};

// What the other product of a link that is neither a component nor a
// variant is to the product, by the relation and which side the product
// stands on: what it uses is a "Used system", what uses it a "User". A
// relation the list does not know reads as it does in the comparison.
const OTHER_ROLES: Record<string, [outgoing: string, incoming: string]> = {
  uses: ["Used system", "User"],
  uses_system: ["Used system", "User"],
  carries_payload: ["Payload", "Carrier"],
  has_member: ["Member", "Group"],
  interfaces_with: ["Interface", "Interface"],
};

export const otherRole = (link: ProductStructureLink): string => {
  const roles = OTHER_ROLES[link.relation];
  return roles ? roles[link.direction === "outgoing" ? 0 : 1] : otherLabel(link);
};

// The composition of a product is read in five parts, each row named by
// what the other is to the product: its components, its payloads, what it
// is built into or carried by, its family of models, and the systems it
// otherwise works with. The deeper chain belongs to the value chain view.

// The texts of a list, each once, in order, read together: words parted by
// a comma, sentences by a space
const distinctText = (
  texts: (string | null | undefined)[],
  separator = ", ",
): string | null =>
  Array.from(new Set(texts.filter((text): text is string => Boolean(text)))).join(separator) ||
  null;

// A payload is mounted on the product (a component of the payload kind) or
// carried by it; the product is a payload of a carrier the other way round
const isMountedPayload = (link: ProductStructureLink): boolean =>
  link.link_kind === "mounted_payload";

const isCarriage = (link: ProductStructureLink): boolean =>
  link.relation === "carries_payload";

// The documents of several links, each once
const linkSources = (links: ProductStructureLink[]): FactSource[] => {
  const byDocument = new Map<string, FactSource>();
  for (const source of links.flatMap((link) => link.sources)) {
    if (!byDocument.has(source.document_id)) {
      byDocument.set(source.document_id, source);
    }
  }
  return Array.from(byDocument.values());
};

// An unnamed component reads as what it does ("4G modem"), else as its class
const unnamedComponent = (link: ProductStructureLink): RelationRow => {
  const row = structureRow("Component")(link, 0);
  return {
    ...row,
    key: link.product.id,
    entity: { ...row.entity, name: link.role ?? row.entity.name },
    detail: link.context ?? null,
    note: link.category?.name ? `unnamed · ${link.category.name}` : "unnamed",
  };
};

// What makes unnamed components one part: their class, or, for ones of no
// class, what they do in the same words; null for one that says neither
const unnamedKey = (link: ProductStructureLink): string | null => {
  if (link.product.name) {
    return null;
  }
  if (link.category) {
    return `class:${link.category.id}`;
  }
  const role = link.role?.trim().toLowerCase();
  return role ? `role:${role}` : null;
};

// Unnamed components that are one part, as one row: most likely the same
// part, reported by several documents. Ones of a class lead to the class,
// what each does read as their function; ones of no class read as what
// they do and lead to the first of them. Their sources are theirs, each
// document once.
const unnamedComponents = (key: string, links: ProductStructureLink[]): RelationRow => {
  const [first] = links;
  const sources = linkSources(links);
  return {
    key: `unnamed-${key}`,
    entity: first.category ?? { ...first.product, name: first.role },
    relation: "Component",
    derived: null,
    summary: distinctText(
      links.map((link) => link.summary),
      " ",
    ),
    sources,
    latest: latestOf(sources),
    category: first.category,
    detail: first.category
      ? distinctText(links.map((link) => link.role))
      : distinctText(links.map((link) => link.context)),
    note: `${links.length} unnamed`,
    count: links.length,
  };
};

/**
 * A product's components as rows, each with the broad kind its class falls
 * in; its mounted payloads are not among them (`payloadRows`). A named
 * component is a row of its own; an unnamed one reads as what it does, and
 * unnamed ones of one class, or of no class and doing the same, are one
 * row, counted.
 */
export const componentRows = (contains: ProductStructureLink[]): RelationRow[] => {
  const links = contains.filter((link) => !isMountedPayload(link));
  // The unnamed components that are one part, gathered first
  const unnamedByKey = new Map<string, ProductStructureLink[]>();
  for (const link of links) {
    const key = unnamedKey(link);
    if (key) {
      unnamedByKey.set(key, [...(unnamedByKey.get(key) ?? []), link]);
    }
  }
  // Then every row in order, the unnamed ones of a part at the first of them
  const rows: RelationRow[] = [];
  links.forEach((link, index) => {
    // A server from before the kinds are read sends none: the class itself
    // groups the component then, so that a component with a class is never
    // said to have none
    const group = link.category_group ?? link.category ?? null;
    const key = unnamedKey(link);
    const same = key ? unnamedByKey.get(key) : undefined;
    if (link.product.name) {
      rows.push({ ...structureRow("Component")(link, index), group });
    } else if (!same) {
      rows.push({ ...unnamedComponent(link), group });
    } else if (key && same[0] === link) {
      rows.push({
        ...(same.length > 1 ? unnamedComponents(key, same) : unnamedComponent(link)),
        group,
      });
    }
  });
  return rows;
};

/**
 * A product's payloads as a plain list, one row per payload: what is
 * mounted on it and what it carries, a payload stated both ways or by
 * several documents once. Its roles ("EO/IR sensor", "radar") are all the
 * documents state, each once, and its sources theirs.
 */
export const payloadRows = (
  contains: ProductStructureLink[],
  other: ProductStructureLink[],
): RelationRow[] => {
  const byProduct = new Map<string, ProductStructureLink[]>();
  for (const link of [
    ...contains.filter(isMountedPayload),
    ...other.filter((link) => isCarriage(link) && link.direction === "outgoing"),
  ]) {
    byProduct.set(link.product.id, [...(byProduct.get(link.product.id) ?? []), link]);
  }
  return Array.from(byProduct.values(), (links) => {
    const [first] = links;
    const sources = linkSources(links);
    return {
      ...structureRow("Payload")(first, 0),
      key: `payload-${first.product.id}`,
      summary: distinctText(
        links.map((link) => link.summary),
        " ",
      ),
      sources,
      latest: latestOf(sources),
      detail: distinctText(links.map((link) => link.role)),
    };
  });
};

/**
 * What a product is built into or carried by: the products, then the
 * classes it typically is part of. A product it is mounted on or carried by
 * as a payload is its carrier.
 */
export const hostRows = (
  builtInto: ProductStructureLink[],
  typicalHosts: ProductTypicalHost[],
  other: ProductStructureLink[] = [],
): RelationRow[] => [
  ...builtInto.map((link, index) =>
    structureRow(isMountedPayload(link) ? "Carrier" : "Host")(link, index),
  ),
  ...other
    .filter((link) => isCarriage(link) && link.direction === "incoming")
    .map(structureRow("Carrier")),
  ...typicalHosts.map((host, index) => ({
    key: `host-${host.category.id}-${index}`,
    entity: host.category,
    relation: "Host class",
    derived: null,
    summary: host.summary,
    sources: host.sources,
    latest: latestOf(host.sources),
    detail: host.basis ? codeWords(host.basis) : null,
  })),
];

/** A product's family: the models it varies, then its variants. */
export const familyRows = (
  variantOf: ProductStructureLink[],
  variants: ProductStructureLink[],
): RelationRow[] => [
  ...variantOf.map(structureRow("Base model")),
  ...variants.map(structureRow("Variant")),
];

/**
 * The systems a product otherwise works with, each by what it is to it;
 * its payloads and carriers are read with its payloads and hosts.
 */
export const otherLinkRows = (other: ProductStructureLink[]): RelationRow[] =>
  other
    .filter((link) => !isCarriage(link))
    .map((link, index) => structureRow(otherRole(link))(link, index));

// A role the list does not know yet comes last
export const roleRank = (role: string): number => {
  const rank = ROLE_ORDER.indexOf(role as OrganizationRole);
  return rank < 0 ? ROLE_ORDER.length : rank;
};

// The roles that make an organization a maker of the product: who
// manufactures it and who develops it, as the maker filter reads them
export const MAKER_ROLES: OrganizationRole[] = ["maker", "developer"];

// One organization that makes the product, with all its maker relations
export interface MakerEntry {
  organization: EntityRef;
  country: string | null;
  // Its maker roles, the manufacturer's first
  roles: OrganizationRole[];
  // The documents behind any of its maker relations, each once
  sources: FactSource[];
  // Null when one of its relations was not derived; else the mark of the
  // derivation, one stated of the product before an inherited one
  derived: DerivedMark | null;
  // Every relation of it is inferred from the portfolio or the family
  likely: boolean;
  // The kinds of organization its record names ("manufacturer", "system
  // integrator")
  types: string[];
}

type RoleRelation = { role: OrganizationRole; item: ProductOrganization };

const makerEntry = (own: RoleRelation[]): MakerEntry => {
  const items = own.map(({ item }) => item);
  const sources = new Map<string, FactSource>();
  for (const source of items.flatMap((item) => item.sources)) {
    if (!sources.has(source.document_id)) {
      sources.set(source.document_id, source);
    }
  }
  const stated = items.some((item) => item.derived === null);
  const marks = items
    .map((item) => item.derived)
    .filter((mark): mark is DerivedMark => mark !== null);
  return {
    organization: items[0].organization,
    country: items.find((item) => item.country)?.country ?? null,
    roles: Array.from(new Set(own.map(({ role }) => role))),
    sources: Array.from(sources.values()),
    derived: stated
      ? null
      : (marks.find((mark) => !isInheritedBasis(mark.basis)) ?? marks[0]),
    likely: !stated && marks.every((mark) => isInheritedBasis(mark.basis)),
    // A response from before the kinds are read has none
    types: items.find((item) => item.types?.length)?.types ?? [],
  };
};

// Where a maker stands: the ones a source attributes the product to
// first, the manufacturers before the developers, the likely makers last
const makerRank = (entry: MakerEntry): number =>
  (entry.likely ? 2 : 0) + (entry.roles.includes("maker") ? 0 : 1);

// The organizations that make the product, one entry each, by rank; within
// a rank the order of the response stands
export const makerEntries = (
  groups: ProductOrganizationGroup[],
): MakerEntry[] => {
  const byOrganization = new Map<string, RoleRelation[]>();
  for (const group of [...groups].sort(
    (left, right) => roleRank(left.role) - roleRank(right.role),
  )) {
    if (!MAKER_ROLES.includes(group.role)) {
      continue;
    }
    for (const item of group.items) {
      const id = item.organization.id;
      byOrganization.set(id, [
        ...(byOrganization.get(id) ?? []),
        { role: group.role, item },
      ]);
    }
  }
  return Array.from(byOrganization.values(), makerEntry)
    .map((entry, index) => ({ entry, index }))
    .sort(
      (left, right) =>
        makerRank(left.entry) - makerRank(right.entry) ||
        left.index - right.index,
    )
    .map(({ entry }) => entry);
};

// The groups of every other role, in the fixed order of the roles
export const otherOrganizationGroups = (
  groups: ProductOrganizationGroup[],
): ProductOrganizationGroup[] =>
  groups
    .filter(
      (group) => !MAKER_ROLES.includes(group.role) && group.items.length > 0,
    )
    .sort((left, right) => roleRank(left.role) - roleRank(right.role));

// The part an organization plays by its relation to the product; a
// relation the list does not know reads as its words ("Tested by")
const RELATION_ROLES: Record<string, string> = {
  operated_by: "Operator",
  procured_by: "Buyer",
  funded_by: "Funder",
  owned_by: "Owner",
  integrated_by: "Integrator",
  supplied_by: "Supplier",
  evaluated_by: "Evaluator",
  maintained_by: "Maintainer",
  other: "Other",
};

export const relationRole = (relation: string): string =>
  RELATION_ROLES[relation] ?? codeLabel(relation);

// The organizations of every role but the makers' as rows, each named by
// the part it plays ("Operator", "Evaluator"): role by role in the fixed
// order, within a role the larger stated quantity, then the better sourced
// first
export const organizationRows = (
  groups: ProductOrganizationGroup[],
): RelationRow[] =>
  otherOrganizationGroups(groups).flatMap((group) =>
    [...group.items]
      .sort(
        (left, right) =>
          (right.quantity ?? -1) - (left.quantity ?? -1) ||
          right.sources.length - left.sources.length,
      )
      .map((item, index) => ({
        key: `${group.role}-${item.organization.id}-${item.relation}-${index}`,
        entity: item.organization,
        relation: relationRole(item.relation),
        derived: item.derived,
        summary: item.summary,
        sources: item.sources,
        latest: latestOf(item.sources),
        quantity: item.quantity,
        quantityText:
          formatQuantity(item.quantity, item.quantity_wording) || null,
        types: item.types ?? [],
        countries: item.country ? [item.country] : [],
      })),
  );
